import { useCallback, useEffect, useState } from 'react';
import {
  usePlaidLink,
  PlaidLinkOnExit,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnEvent,
} from 'react-plaid-link';
import { handleErrorMessage } from 'v3/utils/errorHandler';
import { PaymentClient } from 'v3/api/client/PaymentClient';
import { BankAccountLinkProps } from 'v3/hooks/bankAccountLink';
import { useMixpanel } from 'v3/hooks/useMixpanel';
import { useQuery } from 'v3/hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setPlaidTrigger } from 'leadData/plaidSlice';

// Shared with useStripeBankAccount so the two stay interchangeable — see bankAccountLink.ts.
type ConnectBankAccountProps = BankAccountLinkProps;

type PlaidConfig = {
  onSuccess: (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata
  ) => Promise<void>;
  onExit: PlaidLinkOnExit;
  onEvent: PlaidLinkOnEvent;
  token: string;
};

export const useConnectBankAccount = ({
  cfUuid,
  signContract,
  setIsLoading,
  setIsProcessing,
  onExit,
  clubId,
  userUuid,
  shouldRedirect = true,
  resume = false,
}: ConnectBankAccountProps) => {
  const [plaidConfig, setPlaidConfig] = useState({} as PlaidConfig);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkError, setLinkError] = useState(false);

  const query = useQuery();
  const navigate = useNavigate();
  const mp = useMixpanel();

  const name = query.get('name');
  const dispatch = useDispatch();

  const handleSuccess = useCallback(
    async (plaidPublicToken: string, plaidAccountId: string) => {
      setIsSubmitting(true);

      // const { error } = await PaymentClient().setRecurringPayments({
      //   cfUuid,
      //   plaidPublicToken,
      //   plaidAccountId,
      // });

      try {
        setIsProcessing('processing');
        await signContract({ plaidPublicToken, plaidAccountId });
        setIsProcessing('complete');
        setIsSubmitting(false);
        setIsLoading(false);
        dispatch(setPlaidTrigger({ plaidTrigger: false }));

        if (name && shouldRedirect) {
          navigate(`/payment-success/?name=${name}`);
        }
      }
      catch (error) {
        // Clearing isProcessing is what lets the customer try again: the CTA is disabled
        // while it reads 'processing', so leaving it set turns any failure into a dead end.
        setIsProcessing('');
        setIsSubmitting(false);
        setIsLoading(false);
        dispatch(setPlaidTrigger({ plaidTrigger: false }));

        // signContract already reported this to the customer before throwing — re-reporting
        // here only stacks a second, vaguer toast on top of the real one.
        console.error('Bank account connection failed:', error);
      }
    },
    [signContract]
  );

  const onSuccess = useCallback(async (publicToken: string, metadata: any) => {
    const {
      account_id: accountId,
      account: { verification_status: verificationStatus },
    } = metadata;
    setIsSubmitting(true);
    setLinkError(false);

    if (verificationStatus === 'pending_manual_verification') {
      const { error } = await PaymentClient().setPlaidManualVerification({
        cfUuid,
        plaidPublicToken: publicToken,
        plaidAccountId: accountId,
      });

      if (name && shouldRedirect) {
        navigate(`/microdeposits/?name=${name}`);
      }

      if (error) {
        setIsSubmitting(false);
      }

      mp.fireManualVerification();
    } else {
      // const { error } = await PaymentClient().markBankAccountAsVierified({
      //   cfUuid,
      // });
      // if (error) {
      //   setIsSubmitting(false);
      // }

      mp.registerPlaidSuccess();
      mp.firePlaidSuccess();

      await handleSuccess(publicToken, accountId);
    }

    setIsSubmitting(false);
  }, []);

  const onPlaidLinkExit: PlaidLinkOnExit = useCallback(
    (error, metadata) => {
      if (error) {
        mp.firePlaidError(error.error_message);
        setLinkError(true);
      } else {
        if (metadata.status) {
          mp.firePlaidOnExit(metadata.status);
        } else {
          mp.firePlaidOnExit('Microdeposits Step Incomplete');
        }
      }

      if (onExit) {
        onExit();
      }
    },
    [mp, onExit]
  );

  const createLinkToken = useCallback(async () => {
    const { data, error } = await PaymentClient().createPlaidLinkToken(cfUuid, resume);

    if (error) {
      handleErrorMessage(error);
      return;
    }

    const { linkToken } = data;

    setPlaidConfig({
      token: linkToken,
      onSuccess,
      onExit: onPlaidLinkExit,
      onEvent: (event) => {
        if (event !== 'TRANSITION_VIEW') mp.firePlaidNextStep(event);
      },
    });
  }, [onSuccess, onPlaidLinkExit, cfUuid, mp, resume]);

  useEffect(() => {
    createLinkToken();
  }, [createLinkToken]);

  const { open, ready } = usePlaidLink(plaidConfig);

  return {
    open,
    ready,
    isSubmitting,
    linkError,
  };
};
